import React from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';

import { quemVolta, mereceAPrimeiraDobra } from '../planoDeAcaoApresentacao';

/**
 * A primeira dobra de quem volta: o que mudou, e o que fazer hoje.
 *
 * POR QUE EXISTE. O produto tratava toda visita como se fosse a primeira. A
 * pessoa lançava o mês, via cinco iniciativas, executava duas, voltava três
 * semanas depois e encontrava exatamente a mesma tela — sem marca do que tinha
 * feito, sem prazo, sem nada dizendo que o tempo passou. Um consultor que
 * repete a mesma lista todo mês, ignorando o que o cliente fez no meio,
 * não está acompanhando ninguém.
 *
 * O QUE ELA NÃO FAZ: não inventa novidade. Quando nada mudou, ela diz que nada
 * mudou — e continua sendo motivo para abrir, porque as iniciativas em aberto
 * seguem na mesma frase. Quem nunca esteve aqui não recebe um "desde a última
 * visita" vazio; recebe o começo.
 *
 * O CELULAR MANDA NO QUE APARECE. Este cartão ocupa o mesmo lugar do
 * `ProximoPasso`, e ocupa SOZINHO: dois guias empilhados a 390px empurram o
 * Índice de Saúde para baixo da dobra — defeito que este código já cometeu uma
 * vez e corrigiu. Por isso o parágrafo de contexto e a lista de mudanças só
 * aparecem onde há altura para eles; o título e a ação de hoje, que são o
 * conteúdo, aparecem sempre.
 */
const DesdeAUltimaVisita = ({ retorno, onIrPara, onVerPlano }) => {
  const dobra = quemVolta(retorno);
  // Sem iniciativa em aberto e sem nada a relatar, este cartão não tem o que
  // dizer — e o lugar volta para o guia que ensina o próximo passo do dado. A
  // MESMA condição decide lá no dashboard qual dos dois entra; ela mora numa
  // função só para que não exista o estado em que nenhum aparece.
  if (!dobra || !mereceAPrimeiraDobra(retorno)) return null;

  const hoje = dobra.hoje;

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.75, sm: 2 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: dobra.vencidas > 0 ? 'warning.main' : 'divider',
        bgcolor: 'background.paper'
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1.5, sm: 3 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
              {dobra.estado === 'primeiraVisita' ? 'Seu plano de ação' : 'Desde a última visita'}
            </Typography>
            {dobra.vencidas > 0 && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`${dobra.vencidas} ${dobra.vencidas === 1 ? 'vencida' : 'vencidas'}`}
              />
            )}
          </Stack>

          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {dobra.titulo}
          </Typography>

          {dobra.frase && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: '70ch', mt: 0.25, display: { xs: 'none', sm: 'block' } }}
            >
              {dobra.frase}
            </Typography>
          )}

          {dobra.mudancas.length > 0 && (
            <Stack spacing={0.25} sx={{ mt: 0.5, display: { xs: 'none', sm: 'flex' } }}>
              {dobra.mudancas.map((mudanca) => (
                <Typography key={mudanca.id} variant="body2" color="text.secondary" sx={{ maxWidth: '70ch' }}>
                  {mudanca.texto}
                </Typography>
              ))}
            </Stack>
          )}

          {/* O QUE FAZER HOJE APARECE NO CELULAR TAMBÉM.
              É metade da razão de este cartão existir: quem volta quer saber por
              onde recomeçar. Esconder isso na primeira dobra deixaria só o
              diagnóstico do passado, que é justamente o que o produto já fazia. */}
          {hoje && (
            <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 0.75 }}>
              <PlaylistAddCheckIcon
                fontSize="small"
                color={hoje.vencida ? 'warning' : 'primary'}
                sx={{ mt: 0.25 }}
              />
              <Typography variant="body2" sx={{ maxWidth: '65ch' }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {hoje.vencida ? 'Atrasada: ' : 'Comece por: '}
                </Box>
                {hoje.acao}
              </Typography>
            </Stack>
          )}
        </Box>

        {(onVerPlano || onIrPara) && (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => (onVerPlano ? onVerPlano() : onIrPara())}
            sx={{ minHeight: 44, flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
          >
            Ver o plano
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default DesdeAUltimaVisita;
